import { cn } from '@/lib/utils';
import { NOYAU_CODES, type NoyauCode, type NoyauEnCours } from '@rosetta/shared';
import type { ReactNode } from 'react';
import { LienNoyauEnCours } from './lien-noyau-en-cours';
import { RondChargement } from './noyau-en-cours';
import { PastilleCodeNoyau } from './pastille-code-noyau';

interface VoirAussiNoyauxProps {
  /** The sentence before the list: "Voir aussi les traductions vers :" on the analysis, "Voir aussi :" on W1. */
  libelle: string;
  /** `id` of the sentence, which names the list (`aria-labelledby`): unique in the page. */
  idTitre: string;
  /** The noyaux to list, already translated: the link of each is `lien`. The displayed one is left out by the caller. */
  noyaux: readonly NoyauCode[];
  /** How to link a translated noyau: the analysis goes to its analysis, W1 to its result. */
  lien: (noyau: NoyauCode) => ReactNode;
  /** The noyaux another launch of the document is translating (#36): listed with a ring, towards its waiting screen. */
  enCours?: readonly NoyauEnCours[];
  /** Spacing of the line in its page (nothing is drawn, so no hole, when there is no noyau to list). */
  className?: string;
}

/**
 * "Voir aussi ... :" (#22, #36, W1): the line of pills that leads to the other noyaux of a
 * document, shared by the header of the analysis ("Voir aussi les traductions vers :", each pill
 * a link to the analysis of that noyau on the same tab) and by the waiting screen W1 ("Voir
 * aussi :", each pill a link to the result of that noyau). Nothing when there is no noyau to
 * list; it never offers a noyau that is not translated yet, except the ones another launch of
 * the document is translating (#36): they are in the list, in the charter order, with a turning
 * ring and the name "<noyau>, en cours de traduction", and their link opens the waiting screen
 * of that launch.
 */
export function VoirAussiNoyaux({
  libelle,
  idTitre,
  noyaux,
  lien,
  enCours = [],
  className,
}: VoirAussiNoyauxProps) {
  const lancements = new Map(enCours.map((ligne) => [ligne.noyau, ligne.lancement] as const));
  const autres = NOYAU_CODES.filter((code) => lancements.has(code) || noyaux.includes(code));
  if (autres.length === 0) return null;

  return (
    <div
      className={cn('flex items-center gap-2 text-gray-700 text-small leading-[1.4]', className)}
    >
      <p id={idTitre}>{libelle}</p>
      <ul aria-labelledby={idTitre} className="m-0 flex list-none items-center gap-1 p-0">
        {autres.map((code) => {
          const lancement = lancements.get(code);
          return (
            <li key={code}>
              {lancement === undefined ? (
                lien(code)
              ) : (
                <LienNoyauEnCours
                  noyau={code}
                  lancement={lancement}
                  className="inline-flex h-6 items-center gap-1 rounded-full px-1 hover:bg-gray-100"
                >
                  <PastilleCodeNoyau noyau={code} />
                  <RondChargement />
                </LienNoyauEnCours>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
